import React from "react";
import { ShoppingBag, Store, BadgeCheck, ArrowUpRight } from "lucide-react";
import { Reveal, CountUp, Section } from "@/components/landing/kit";
import { STATS, PLATFORMS } from "@/lib/site-data";

const PARTNERS = [
  {
    key: "amazon",
    tag: "Official Partner",
    name: "Amazon Advertising & SPN",
    cta: "Find us on Amazon",
    href: "https://advertising.amazon.com/partners/directory/details/amzn1.ads1.ma1.c1doroals8gqwv1iend510raa/DSA-eCommerce-LLC?sref_=suggestion",
    color: "#FF9900",
    Icon: ShoppingBag,
  },
  {
    key: "walmart",
    tag: "Official Partner",
    name: "Walmart Solution Provider",
    cta: "Find us on Walmart",
    href: "https://marketplace.walmart.com/solution-providers-old/dsaecommerce/",
    color: "#0071DC",
    Icon: Store,
  },
];

export const PartnerStrip = () => (
  <section
    data-testid="partner-strip"
    className="border-b border-[#E5E8F0] bg-[#F4F6FB] py-14 sm:py-16"
  >
    <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
      <Reveal className="mb-8 flex items-center justify-center gap-2 text-center">
        <BadgeCheck className="h-4 w-4 text-[#2D5BFF]" />
        <p className="font-heading text-xs font-bold uppercase tracking-[0.3em] text-[#6B7385]">
          Verified marketplace partner status
        </p>
      </Reveal>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {PARTNERS.map((p, i) => {
          const Icon = p.Icon;
          return (
            <Reveal
              key={p.key}
              delay={i * 0.08}
              className="group"
            >
              <a
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`partner-${p.key}`}
                className="flex h-full items-center gap-4 rounded-2xl border border-[#E5E8F0] bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:p-6"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${p.color}80`)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E8F0")}
              >
                <span
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${p.color}18`, color: p.color }}
                >
                  <Icon className="h-7 w-7" strokeWidth={2.2} />
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className="flex items-center gap-1.5 font-heading text-[10px] font-bold uppercase tracking-[0.22em]"
                    style={{ color: p.color }}
                  >
                    <BadgeCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
                    {p.tag}
                  </p>
                  <p className="mt-1 font-heading text-lg font-bold leading-tight text-[#0F1117] sm:text-xl">
                    {p.name}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-xs font-medium text-[#5B6375] transition-colors group-hover:text-[#0F1117] sm:text-sm">
                    {p.cta}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </p>
                </div>
              </a>
            </Reveal>
          );
        })}
      </div>

      {/* Platform logo strip */}
      <Reveal className="mt-12" delay={0.1}>
        <p className="mb-5 text-center font-heading text-[11px] font-bold uppercase tracking-[0.28em] text-[#858D9E]">
          Stores we build, stock &amp; run for you
        </p>
        <div
          data-testid="platform-logo-strip"
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        >
          {PLATFORMS.map((p) => (
            <div
              key={p.key}
              data-testid={`platform-logo-${p.key}`}
              title={p.name}
              className="flex items-center justify-center rounded-xl border border-[#E5E8F0] bg-white px-4 py-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${p.color}66`)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E8F0")}
            >
              <img
                src={p.src}
                alt={`${p.name} logo`}
                className={`w-auto object-contain ${
                  p.key === "temu" ? "h-10 rounded-lg sm:h-11" : "h-8 sm:h-9"
                }`}
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export const StatsBand = () => (
  <Section className="border-b border-[#E5E8F0] bg-white py-16 sm:py-20">
    <div className="grid grid-cols-2 gap-y-10 lg:grid-cols-4">
      {STATS.map((s) => (
        <Reveal key={s.label} className="text-center lg:border-l lg:border-[#E5E8F0] lg:first:border-l-0">
          <div className="font-heading text-5xl font-black tracking-tight text-[#2D5BFF] sm:text-6xl">
            <CountUp
              value={s.value}
              prefix={s.prefix || ""}
              suffix={s.suffix || ""}
              decimals={s.decimals || 0}
            />
          </div>
          <p className="mx-auto mt-3 max-w-[12rem] text-sm font-medium leading-snug text-[#5B6375]">
            {s.label}
          </p>
        </Reveal>
      ))}
    </div>
  </Section>
);
